// Real, approved SWITCH case studies. Each opens a detail page at /our-work/<slug>.
export const CASE_STUDIES = [
  {
    slug: "data-into-growth",
    kicker: "Data into Growth",
    client: "Premium Food Retail Chain",
    service: "Customer Revenue Expansion",
    location: "Mumbai, Pune & Bangalore, India",
    situation: "Growth Has Stalled",
    image: "/assets/case-data-into-growth.jpg",
    headline: "Turning fragmented customer data into 54% revenue growth",
    opportunity: [
      "A leading food retailer had accumulated a large customer database, but inconsistent and incomplete information limited its ability to understand and engage its loyal customers.",
      "The opportunity was to turn this fragmented data into actionable customer intelligence and use it to increase purchase frequency, category penetration and customer lifetime value.",
    ],
    role: [
      "Analysed and enriched a database of 300,000 customers",
      "Built richer demographic, behavioural and psychographic profiles",
      "Created seven distinct customer personas",
      "Mapped personas against relevant product categories and price points",
      "Designed personalised discovery, upsell and cross-sell journeys",
      "Activated targeted digital campaigns and persona-specific landing pages",
      "Connected digital engagement with in-store offers, coupons and home delivery",
    ],
    metrics: [
      { metric: "54%", label: "growth in revenue" },
      { metric: "1.5×", label: "increase in store visits" },
      { metric: "20%", label: "growth in online shopping" },
    ],
    outcomes: [
      "Pilot successfully executed across 3 cities",
      "Growth achieved over 6 months",
    ],
  },
  {
    slug: "behaviour-into-growth",
    kicker: "Behaviour into Growth",
    client: "Topical Pain Management Brand",
    service: "Communications Strategy",
    location: "Sub-Saharan Africa",
    situation: "Reposition & Transform",
    image: "/assets/case-behaviour-into-growth.jpg",
    headline: "Turning pain relief into a reason to switch to herbal",
    opportunity: [
      "Pain relief was often associated with pill consumption. The opportunity was to challenge this behaviour and position the brand's topical pain-relief portfolio as a herbal alternative.",
    ],
    role: [
      "Developed the communication strategy around switching from pills to a herbal remedy",
      "Created TVCs across Balm, Universal Oil and Ultra Power Balm",
      "Built a consistent switch narrative across the portfolio",
      "Used mass communication to drive awareness and consideration",
    ],
    metrics: [
      { metric: "30%", label: "month-on-month growth" },
    ],
    outcomes: [],
  },
  {
    slug: "efficiency-into-growth",
    kicker: "Efficiency into Growth",
    client: "MEP Distributor",
    service: "Growth & Portfolio Transformation",
    location: "Middle East",
    situation: "Create Enterprise Value",
    image: "/assets/case-efficiency-into-growth.jpg",
    headline: "Turning portfolio complexity into 61% top-line growth",
    opportunity: [
      "The business needed to accelerate revenue growth while improving bottom-line efficiency across its portfolio.",
    ],
    role: [
      "Identified opportunities to unlock top-line growth",
      "Consolidated the portfolio to improve business efficiency",
      "Rationalised offerings to sharpen commercial focus",
      "Aligned growth priorities around stronger revenue and profitability",
    ],
    metrics: [
      { metric: "61%", label: "growth in top-line revenue" },
      { metric: "$24M → $40M", label: "sales scaled" },
    ],
    outcomes: [],
  },
  {
    slug: "opportunity-into-growth",
    kicker: "Opportunity into Growth",
    client: "Personal Care Startup",
    service: "Brand Creation · Business Strategy · D2C",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-opportunity-into-growth.jpg",
    headline: "Turning a market gap into a clinically tested herbal personal care brand",
    opportunity: [
      "The founders identified a gap between the growing demand for herbal personal care and products that could substantiate their safety and efficacy claims.",
      "The opportunity was to build a differentiated personal care brand around truly herbal, clinically tested products and take it from concept to market.",
    ],
    role: [
      "Assessed market opportunity and business feasibility",
      "Created the brand, identity and brand structure",
      "Helped shape product formulation and testing strategy",
      "Identified and collaborated with manufacturing partners",
      "Developed packaging and communication",
      "Built the D2C and go-to-market strategy",
      "Established the technology and e-commerce ecosystem",
      "Enabled online and offline distribution and retail expansion",
    ],
    metrics: [
      { metric: "$300K+", label: "purchase orders secured" },
    ],
    outcomes: [
      "Built the brand end-to-end, from feasibility to market",
      "Established online and offline distribution channels",
      "Created a scalable foundation for D2C and retail growth",
    ],
  },
  {
    slug: "innovation-into-growth",
    kicker: "Innovation into Growth",
    client: "Fertility Care Startup",
    service: "Brand & Communications Strategy · Demand Gen Strategy",
    location: "India",
    situation: "Reposition & Transform",
    image: "/assets/case-innovation-into-growth.jpg",
    headline: "Turning fertility tracking into a journey of hope and assurance",
    opportunity: [
      "The company wanted to simplify fertility tracking with a scientifically backed, user-friendly device that helped couples better understand their ovulation cycles and plan conception.",
      "The challenge was to make a technology-led proposition feel simple, credible and reassuring during an emotionally demanding journey.",
    ],
    role: [
      "Defined the brand idea around \u201CHope and Assurance: Reality\u2019s True Lens\u201D",
      "Created the brand identity inspired by the idea of a lens, focus and clarity",
      "Developed a vibrant visual language built around hope, optimism and assurance",
      "Designed the website around intuitive access to information",
      "Built a research-led content and blog ecosystem to strengthen credibility",
      "Developed film communication around the emotional realities of the fertility journey",
      "Created a consistent brand experience across identity, digital, content and communication",
    ],
    metrics: [
      { metric: "3,459", label: "devices pre-booked" },
    ],
    outcomes: [
      "Selected for and inducted into Y Combinator",
    ],
  },
  {
    slug: "access-into-reach",
    kicker: "Access into Reach",
    client: "Training Resource Package for Family Planning",
    service: "Platform Access & Content Delivery Strategy",
    location: "Global · Low-connectivity markets",
    situation: "Ready to Scale",
    image: "/assets/case-access-into-reach.jpg",
    headline: "Making critical medical training accessible to 63.8M people",
    opportunity: [
      "The Training Resource Package was built as a global, evidence-based resource for trainers, supervisors and programme managers delivering family-planning training, including nurses, midwives and other frontline healthcare providers.",
      "The opportunity was to make this extensive body of training modules, guides and clinical resources genuinely accessible in regions where unreliable internet, limited bandwidth and lower-specification devices could otherwise make a digital platform difficult to use.",
      "The challenge was therefore not simply putting medical knowledge online. It was designing access around the realities of the people who needed it most.",
    ],
    role: [
      "Translated low-bandwidth, intermittent connectivity and device limitations into core product and architecture requirements",
      "Developed a headless web and CMS platform that allowed training modules and files to be centrally created, managed and continuously updated",
      "Built caching and retrieval logic around frequently accessed resources to reduce repeated dependence on network connectivity",
      "Optimised content delivery for lower-specification phones and devices rather than designing only for high-end smartphones and broadband environments",
      "Structured the platform to support a large and varied library of modules, guides, presentations, handouts and training tools across different family-planning methods",
      "Added bookmarking and discussion functionality so the platform could support continued learning and reference, not merely one-time content downloads",
      "Created a scalable technology architecture separating the user-facing application from the CMS and underlying services, allowing the resource ecosystem to evolve without rebuilding the entire experience",
    ],
    metrics: [
      { metric: "63.8M", label: "people reached" },
      { metric: "24.6M", label: "training materials accessed" },
    ],
    outcomes: [],
  },
  {
    slug: "romance-into-relevance",
    kicker: "Romance into Relevance",
    client: "A Global Personal Care Brand",
    service: "Brand Evolution & Repositioning",
    location: "Multi-market",
    situation: "Reposition & Transform",
    image: "/assets/case-romance-into-relevance.jpg",
    headline: "Repositioning a heritage fragrance brand for a new generation, driving 16% sales growth",
    opportunity: [
      "The brand had long been associated with French romance, but that heritage was beginning to work against it. Younger consumers increasingly saw it as an inherited choice, a brand associated with their mothers rather than their own identity.",
      "At the same time, the body-lotion category remained largely functional, centred on hydration and ingredients, while fragrance, one of the brand's strongest equities, was under-leveraged.",
      "The opportunity was therefore larger than relaunching a product. It was to recode the meaning of the brand itself: from passive, inherited romance to modern, self-owned romance, while making fine French fragrance the emotional and strategic centre of the proposition.",
    ],
    role: [
      "Reframed the brand from a legacy romance name into a more contemporary expression of femininity, individuality and control",
      "Elevated fine French fragrance from a supporting product claim into the central driver of brand meaning, interaction and identity",
      "Built a new creative platform around the idea of \u201Cfragrance as quiet control\u201D, reversing traditional category dynamics by portraying the woman as the one setting the terms of attraction",
      "Connected the product's silky-skin benefit with fragrance to create a more sensorial, multi-dimensional proposition rather than another functional body-lotion story",
      "Extended the repositioning beyond communication through refreshed packaging, a new visual language, social and influencer amplification, and a multi-market rollout",
    ],
    metrics: [
      { metric: "16%", label: "sales growth (US$12.24M \u2192 US$14.21M)" },
      { metric: "+1.9 pts", label: "market-share gain (6.5% \u2192 8.4%)" },
      { metric: "40%", label: "YouTube engagement rate" },
      { metric: "31%", label: "YouTube video completion rate" },
    ],
    outcomes: [
      "The campaign emerged as the brand's highest-performing campaign in the last 8 years, while helping move it from inherited romance towards a more relevant, self-defined expression for younger consumers.",
    ],
  },
  {
    slug: "heritage-into-relevance",
    kicker: "Heritage into Relevance",
    client: "A Global Fragrance Brand",
    service: "Brand Evolution & Repositioning",
    location: "GCC",
    situation: "Reposition & Transform",
    image: "/assets/case-heritage-into-relevance.jpg",
    headline: "Reframing natural fragrance for a new generation, driving 9.5% sales growth",
    opportunity: [
      "The brand had more than 250 years of fragrance heritage and strong consumer trust, but across GCC markets that heritage was increasingly becoming a liability. It was associated with an ageing consumer base, while its floral fragrances and natural formulations were being perceived as dated, soft and lacking the intensity younger consumers associated with contemporary premium fragrance.",
      "The opportunity was not to abandon what made the brand distinctive, but to reinterpret it. We needed to take its strongest equities, natural ingredients, florals and British fragrance expertise, and make them relevant to a younger woman seeking individuality, confidence and presence.",
      "The strategic shift was from decorative femininity to powerful femininity: repositioning natural fragrance from something soft and nostalgic into something dynamic, expressive and impossible to ignore.",
    ],
    role: [
      "Repositioned the brand's natural and floral heritage from a legacy association into a contemporary expression of confidence, authority and presence",
      "Turned naturally derived floral ingredients from a functional product attribute into the central strategic idea, positioning nature as a source of energy, movement and impact",
      "Developed a new communication platform around the idea that nature amplifies her energy and fragrance signals undeniable presence",
      "Reframed the role of fragrance from something that decorates or enhances a woman to something that announces her arrival and reflects her identity",
      "Subverted conventional fragrance-category codes by portraying the woman as a figure of achievement and authority rather than an object of admiration, culminating in the reveal of her as the youngest CEO",
      "Extended the repositioning through a refreshed visual language, social and influencer amplification, and a multi-market rollout",
    ],
    metrics: [
      { metric: "9.5%", label: "sales growth (US$909K \u2192 US$995K)" },
      { metric: "+0.2 pts", label: "market-share gain (2.0% \u2192 2.2%)" },
      { metric: "+0.5 pts", label: "numeric distribution (6.5% \u2192 7.0%)" },
      { metric: "50%", label: "YouTube engagement rate" },
      { metric: "40%", label: "YouTube video completion rate" },
    ],
    outcomes: [
      "The repositioning helped move the brand beyond heritage nostalgia, making natural and floral feel contemporary, premium and powerful while presenting women as figures of authority and recognition rather than passive subjects of admiration.",
    ],
  },
  {
    slug: "occasion-into-growth",
    kicker: "Occasion into Growth",
    client: "A Global Stout Brand",
    service: "Growth Strategy \u00B7 Consumer Experience",
    location: "East Africa",
    situation: "Growth Has Stalled",
    image: "/assets/case-occasion-into-growth.jpg",
    headline: "Extending the drinking occasion, driving +1.2 units in average consumption per head",
    opportunity: [
      "The brand wanted to grow its share in East Africa by increasing average consumption by one additional unit per consumer, per occasion.",
      "Market immersion revealed a more nuanced barrier. Stout commanded strong respect for its richness, strength and heritage, but those same equities were also limiting the drinking occasion. Consumers typically stopped after one to two units before moving to lighter lagers as the session continued.",
      "The opportunity was therefore not to change the product, price or positioning, but to redesign the occasion around the product, addressing satiety, pace, food and social behaviour so stout could remain relevant for longer.",
    ],
    role: [
      "Conducted market immersion across Nairobi, Kisumu and Mombasa to understand the behaviours shaping consumption across different drinking occasions",
      "Identified satiety, fragmented drinking occasions and heavier food pairings as key barriers preventing consumers from staying with stout through the full session",
      "Developed lighter, shareable food pairings designed to reduce heaviness and create a more natural rhythm for continued consumption",
      "Created The Split Challenge, a group-based activation that transformed drinking from an individual behaviour into a shared social ritual built around camaraderie and participation",
      "Built the growth framework around four connected levers, food, pace, social interaction and cultural relevance, rather than relying on conventional promotional mechanics",
      "Developed a broader cultural platform rooted in entrepreneurship and East Africa's ambition-driven culture, giving the brand a more contemporary role beyond the drinking occasion",
      "Reframed stout from a drink associated with ending the session into one capable of carrying the night",
    ],
    metrics: [
      { metric: "+1.2 units", label: "average consumption per head (~2.0 \u2192 3.2 units)" },
    ],
    outcomes: [
      "Extended dwell time across participating outlets",
      "Increased engagement by bringing individual drinkers into collective participation and shared rituals",
      "Shifted the role of stout from a \u201Cclosing drink\u201D to one that could remain part of a longer session",
      "Strengthened cultural relevance through an entrepreneurship-led platform that connected the brand with ambition, progress and contemporary East African identity",
    ],
  },
  {
    slug: "concept-into-opportunity",
    kicker: "Concept into Opportunity",
    client: "Luxury Travel & Hospitality Startup",
    service: "Master Planning \u00B7 Brand Strategy \u00B7 Identity \u00B7 Visualisation",
    location: "Middle East",
    situation: "Create Enterprise Value",
    image: "/assets/case-concept-into-opportunity.jpg",
    headline: "Turning a private-island vision into a luxury ecosystem built on exclusivity, privacy and blockchain",
    opportunity: [
      "The ambition was to create a private luxury island for the world's most discerning HNIs and ultra-high-net-worth individuals, a destination where extraordinary experiences, absolute discretion and a futuristic commercial ecosystem came together.",
      "The concept went beyond hospitality. The island was envisioned as a self-contained luxury economy, with its commercial ecosystem operating on blockchain and transactions powered by a proprietary cryptocurrency, creating an additional layer of privacy, exclusivity and control.",
      "At concept stage, however, this existed largely as an ambitious vision.",
      "The opportunity was to transform it into a credible, tangible and investable proposition, one that could bring together the island master plan, hospitality experiences, commercial ecosystem, blockchain infrastructure, brand and digital presence into one compelling world.",
    ],
    role: [
      "Developed the overall island master plan, translating the initial vision into a structured destination with clearly defined hospitality, leisure, retail and membership experiences",
      "Created the name and brand identity around the idea of infinite possibilities, giving the destination a distinctive organising thought rather than positioning it as another luxury resort",
      "Designed an interconnected luxury ecosystem spanning underwater accommodation, a cruise experience, an exclusive members' club, curated retail and other high-end experiences",
      "Helped shape the concept of a blockchain-powered commercial ecosystem, where transactions across the destination could operate through a proprietary cryptocurrency",
      "Developed the identity for the proposed cryptocurrency, integrating it into the broader destination and brand ecosystem rather than treating it as a standalone financial product",
      "Built the proposition around four defining ideas, exclusivity, privacy, luxury and futurism, ensuring the physical experience, commercial model and brand expressed the same world",
      "Developed detailed 3D visualisations that transformed an unbuilt island into something investors and future customers could see, understand and experience before development",
      "Extended the visual identity across the island, hospitality experiences, club, retail, digital currency and supporting brand touchpoints to create one cohesive ecosystem",
      "Designed and developed a modern responsive website to communicate the proposition to prospective investors and future customers",
      "Turned an ambitious idea into a more structured, visualised and investable destination concept with a clear brand, experience architecture and commercial logic",
    ],
    metrics: [],
    outcomes: [
      "Under development, with the master plan, brand ecosystem, digital experience and 3D visualisation delivered",
      "Created a coherent master plan and brand ecosystem capable of moving the project from an abstract vision towards investor and customer engagement",
      "Established a differentiated proposition combining ultra-luxury hospitality, privacy and a blockchain-enabled commercial ecosystem",
      "Made the future destination tangible before construction through an integrated system of master planning, identity, digital experience and 3D visualisation",
    ],
  },
  {
    slug: "innovation-into-growth-wellness",
    kicker: "Innovation into Growth",
    client: "Insect Repellent & Wellness Startup",
    service: "Product Strategy \u00B7 Identity \u00B7 Technology",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-innovation-into-growth-wellness.jpg",
    headline: "Turning insect protection into a connected wellness experience",
    opportunity: [
      "The insect repellent category was still largely defined by one-dimensional chemical protection, products designed to keep insects away, often with little consideration for fragrance, ambience, user control or environmental impact.",
      "The opportunity was to rethink the category altogether: creating a natural, eco-conscious repellent system that could combine effective insect protection with aromatherapy, intelligent dispensing and a more considered sensory experience.",
      "Rather than designing another repellent, the ambition was to create a product that could move naturally across protection, wellness and modern living, relevant at home, at work, while travelling and outdoors.",
    ],
    role: [
      "Identified the opportunity to move insect repellence beyond functional protection by combining it with fragrance, aromatherapy and wellness",
      "Developed the core product concept around a natural aromatic repellent system, creating a more pleasant and sensorial alternative to conventional chemical-led solutions",
      "Integrated smart dispensing technology with app-based controls, giving users greater control over how and when the product operates",
      "Enabled users to customise both fragrance intensity and repellent strength, allowing the experience to adapt to different environments, needs and moments",
      "Designed the proposition for multiple use cases across homes, offices, cars, hotels and outdoor spaces, expanding the potential role of the product beyond a single room or occasion",
      "Developed a refillable product system intended to reduce single-use plastic and build greater sustainability into the proposition from the outset",
      "Created the brand identity and visual language around the intersection of nature, technology and contemporary living",
      "Built the overall proposition around three connected benefits, protection, wellness and sensory experience, creating the foundation for a new kind of insect-repellent offering",
    ],
    metrics: [],
    outcomes: [
      "Awaiting market launch, with the product proposition, brand identity and technology concept developed",
      "Created a differentiated product proposition that moves the category from conventional insect protection towards a broader smart wellness ecosystem",
      "Built a scalable concept capable of extending across multiple environments, fragrance profiles and usage occasions",
      "Combined natural repellence, connected technology, customisation and refillability into one integrated product and brand system",
    ],
  },
  {
    slug: "innovation-into-growth-ayurveda",
    kicker: "Innovation into Growth",
    client: "Skincare Startup",
    service: "Brand Strategy \u00B7 Identity \u00B7 Go-to-Market",
    location: "USA",
    situation: "Ready to Scale",
    image: "/assets/case-innovation-into-growth-ayurveda.jpg",
    headline: "Turning Ayurveda into a high-performance prestige skincare proposition",
    opportunity: [
      "The skincare market often forces a choice between two worlds: traditional natural wellness on one side, and science-led performance on the other.",
      "The opportunity was to bridge that divide by creating a gender-neutral prestige skincare brand that could combine the philosophy of Ayurveda with advanced scientific extraction, measurable efficacy and a contemporary luxury experience.",
      "The ambition was not to simply modernise Ayurveda. It was to build a proposition where Ayurvedic principles, cellular extraction technology, sustainability and personalisation could work together as one differentiated system.",
    ],
    role: [
      "Defined the positioning around high-performance Ayurvedic skincare, moving the proposition beyond traditional herbal cues into a more premium, efficacy-led space",
      "Developed the brand strategy, identity and visual language to balance heritage, science, purity and modern luxury",
      "Created a proprietary proposition combining Ayurvedic ingredients and principles with advanced cellular extraction technology, giving the brand a stronger scientific and performance-oriented story",
      "Introduced face sculpting as part of the skincare ritual, expanding the experience from product application into a more distinctive wellness and beauty practice",
      "Developed a salon-first market-entry strategy, using expert-led environments to build credibility, trial and education before broader D2C expansion",
      "Created a luxury packaging system built around refillability and sustainability, allowing the physical product experience to reinforce the brand's environmental positioning",
      "Built the digital and D2C strategy around personalised skincare, moving the brand away from a one-size-fits-all product journey",
      "Integrated Dosha analysis and AI-driven product recommendations into the proposed digital experience, using technology to translate Ayurvedic personalisation into a more intuitive contemporary format",
      "Connected product, ritual, technology and go-to-market into one coherent prestige proposition rather than treating them as separate innovation layers",
    ],
    metrics: [],
    outcomes: [
      "Awaiting market launch, with the brand strategy, identity and go-to-market approach developed",
      "Created a differentiated skincare proposition at the intersection of Ayurveda, science, luxury and personalisation",
      "Built a scalable route-to-market strategy that could establish credibility through salons before expanding into D2C",
      "Developed the foundation for a more personalised skincare ecosystem combining Dosha-led understanding, advanced extraction technology, AI-assisted recommendations and sustainable luxury",
    ],
  },
  {
    slug: "structure-into-scale",
    kicker: "Structure into Scale",
    client: "Fintech Startup",
    service: "Business Strategy \u00B7 Brand \u00B7 Platform \u00B7 Go-to-Market",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-structure-into-scale.jpg",
    headline: "Building a property fintech group and scaling it to US$10.4M in 6 months",
    opportunity: [
      "The ambition was to build a property-focused fintech group around some of the biggest financial decisions consumers and investors make: borrowing, buying and investing.",
      "The opportunity was larger than launching a single platform. It required structuring the business itself around distinct customer needs, commercial models and revenue opportunities while ensuring they all worked under one coherent group proposition.",
      "We therefore built the business around three core verticals: Mortgage, Property Purchase & Investments, and Commercial Space Investment, each with its own role, customer journey and operating logic, but connected through one brand and growth system.",
    ],
    role: [
      "Structured the overall business architecture and defined the three core verticals that would form the foundation of the group",
      "Developed the business and brand positioning, creating a clear proposition capable of bringing multiple property-finance and investment needs under one coherent identity",
      "Created the complete brand identity and visual language across customer, partner, digital and physical touchpoints",
      "Designed and developed the digital platform to support discovery, lead generation and distinct customer journeys across mortgage, property and commercial investment",
      "Built the sales processes, systems and operating workflows required to convert demand into transactions rather than treating technology as a standalone front end",
      "Developed partner programmes and ecosystem structures to support sourcing, referrals, property opportunities and financial-product distribution",
      "Created the demand-generation strategy, advertising and communications required to build awareness, consideration and qualified leads across the group",
      "Designed customer-facing spatial environments to ensure the offline experience reflected the same proposition and standards as the digital brand",
      "Connected business strategy, brand, technology, sales, partnerships and demand generation into one integrated commercial engine",
    ],
    metrics: [
      { metric: "US$10.4M", label: "revenue generated within 6 months of building and activating the business" },
    ],
    outcomes: [
      "Established a fully structured property fintech group with multiple revenue engines under one coherent proposition",
      "Built the business architecture, brand, platform, sales system, partner ecosystem and demand engine required to move from concept to commercial scale",
    ],
  },
  {
    slug: "concept-into-scale",
    kicker: "Concept into Scale",
    client: "Wedding Planning & Events Startup",
    service: "Business Strategy \u00B7 Brand \u00B7 Platform \u00B7 Go-to-Market",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-concept-into-scale.jpg",
    headline: "Turning wedding planning into a US$2.9M business",
    opportunity: [
      "The ambition was to build a wedding planning and events business from the ground up, not simply create another event-services company.",
      "The opportunity was to organise a highly fragmented category into a more structured, customer-friendly proposition that could bring together discovery, planning, partner coordination and execution under one brand.",
      "To make that commercially viable, the business needed more than a strong identity. It required the complete operating and growth ecosystem behind it: positioning, platform, partner network, sales systems, communications and demand generation.",
    ],
    role: [
      "Structured the business proposition and defined how the brand would compete and create value within the wedding planning and events category",
      "Developed the complete brand positioning, identity and visual language to create a differentiated and credible presence from launch",
      "Designed and developed the digital platform to support discovery, enquiries and conversion across the customer journey",
      "Built the sales process and systems required to manage leads, opportunities, follow-ups and conversion in a disciplined manner",
      "Developed partner programmes to create a scalable ecosystem of planners, venues, specialists and service providers",
      "Created the demand-generation strategy and advertising framework to consistently drive qualified enquiries into the business",
      "Built the communication system across brand, digital and performance channels to move customers from inspiration to action",
      "Connected business strategy, brand, platform, partnerships, sales and demand generation into one integrated commercial engine",
    ],
    metrics: [
      { metric: "US$2.9M", label: "in business generated" },
    ],
    outcomes: [
      "Built the wedding planning business end-to-end, from positioning and identity to platform, partner ecosystem, sales systems and demand generation",
      "Created a structured growth model capable of turning a new category proposition into significant commercial scale",
    ],
  },
  {
    slug: "system-into-growth",
    kicker: "System into Growth",
    client: "Social Development Initiative",
    service: "Social Impact \u00B7 Strategy \u00B7 Identity \u00B7 Digital",
    location: "India",
    situation: "Reposition & Transform",
    image: "/assets/case-system-into-growth.jpg",
    headline: "Turning urban complexity into a blueprint for self-sustaining communities",
    opportunity: [
      "A large slum in an Indian state faced interconnected challenges around infrastructure, resources, livelihoods and long-term self-sufficiency.",
      "The opportunity was to rethink the conventional model of external aid by recognising the skills and capabilities already present within the community, and create a framework through which these could contribute towards a more self-sustaining \u201CSmart Slum\u201D.",
    ],
    role: [
      "Shaped the overall construct and architecture of the initiative",
      "Developed the strategic idea of creating order from chaos by harnessing capabilities already present within the community",
      "Built the framework around skills such as carpentry, plumbing and electrical work to encourage greater self-sufficiency",
      "Developed the \u201CSmart Slum to Smarter City\u201D proposition as a new approach to inclusive urban development",
      "Created the project identity around the idea of order emerging from complexity",
      "Used the lotus petal, fractal patterns and sacred geometry to develop a distinctive visual system",
      "Designed and developed a digital portal for information dissemination and collaboration",
      "Created a platform through which the initiative and its vision could be communicated to stakeholders",
    ],
    metrics: [],
    outcomes: [
      "Blueprint submitted",
    ],
  },
  {
    slug: "sensitivity-into-growth",
    kicker: "Sensitivity into Growth",
    client: "Women's Intimate Hygiene Brand",
    service: "Packaging & Shelf Impact Strategy",
    location: "GCC",
    situation: "Ready to Scale",
    image: "/assets/case-sensitivity-into-growth.jpg",
    headline: "Bringing intimate hygiene into a culturally sensitive market with discretion and trust",
    opportunity: [
      "Launching a women's intimate hygiene range in the GCC required navigating a category that is often treated with considerable privacy and discretion.",
      "The opportunity was to create packaging that could communicate hygiene, efficacy and premium quality without becoming overly explicit, while remaining sensitive to the cultural context of the market.",
    ],
    role: [
      "Developed the packaging approach around discretion, purity and premium appeal",
      "Brought product ingredients to the forefront, highlighting ingredients that are recognised and valued within the region",
      "Used ingredient-led storytelling to make the range easier to understand without relying on overt intimate-hygiene cues",
      "Created a minimal, clean design language suited to a visually sensitive category",
      "Used a distinctive white backdrop to reinforce cues of hygiene, cleanliness and trust",
      "Balanced shelf standout with the discretion expected from the category",
      "Created a cohesive visual system across the intimate hygiene range",
      "Designed the packaging to establish trust while retaining a contemporary, premium aesthetic",
    ],
    metrics: [],
    outcomes: [
      "Awaiting market launch",
    ],
  },
  {
    slug: "efficiency-into-growth-steel",
    kicker: "Efficiency into Growth",
    client: "Leading Steel Manufacturer",
    service: "Business Growth Strategy",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-efficiency-into-growth-steel.jpg",
    headline: "Turning operational complexity into consistent production growth",
    opportunity: [
      "As market demand increased, operational complexity began limiting business growth. Disconnected processes, inconsistent planning and limited operational visibility made it difficult to improve productivity while maintaining quality. The opportunity was to simplify operations, strengthen execution and build a scalable operating model that could support long-term growth.",
    ],
    role: [
      "Identified the operational barriers slowing business growth.",
      "Developed a unified strategy to improve visibility and coordination.",
      "Enabled faster and more informed operational decision-making.",
      "Built a scalable foundation for continuous business improvement.",
    ],
    metrics: [
      { metric: "24%", label: "growth in operational efficiency" },
      { metric: "87% → 96%", label: "on-time production performance" },
    ],
    outcomes: [],
  },
  {
    slug: "precision-into-growth",
    kicker: "Precision into Growth",
    client: "Leading Plywood Manufacturer",
    service: "Business Growth Strategy",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-precision-into-growth.jpg",
    headline: "Turning manufacturing variability into dependable business performance",
    opportunity: [
      "Increasing customer expectations placed greater pressure on maintaining consistent product quality while improving manufacturing efficiency. Variations in planning, production execution and resource utilization affected delivery performance and profitability. The opportunity was to create a more disciplined and predictable operating model that supported sustainable growth.",
    ],
    role: [
      "Identified opportunities to improve operational consistency.",
      "Simplified planning and execution across manufacturing operations.",
      "Improved visibility into business-critical performance indicators.",
      "Enabled continuous operational improvements across the organization.",
    ],
    metrics: [
      { metric: "19%", label: "improvement in manufacturing efficiency" },
      { metric: "82% → 95%", label: "on-time order fulfilment" },
    ],
    outcomes: [],
  },
  {
    slug: "reliability-into-growth",
    kicker: "Reliability into Growth",
    client: "Leading Agro Chemical Manufacturer",
    service: "Business Growth Strategy",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-reliability-into-growth.jpg",
    headline: "Turning operational uncertainty into predictable business outcomes",
    opportunity: [
      "Rapid business expansion increased operational complexity across production and supply operations. Limited coordination and inconsistent planning created challenges in maintaining reliability, meeting customer demand and sustaining profitable growth. The opportunity was to build a more resilient and dependable operating framework.",
    ],
    role: [
      "Identified process gaps affecting operational reliability.",
      "Improved coordination across critical business functions.",
      "Strengthened planning through better operational visibility.",
      "Established a scalable model for long-term business growth.",
    ],
    metrics: [
      { metric: "27%", label: "improvement in supply reliability" },
      { metric: "84% → 97%", label: "production planning accuracy" },
    ],
    outcomes: [],
  },
  {
    slug: "consistency-into-growth",
    kicker: "Consistency into Growth",
    client: "Leading PVC Pipe Manufacturer",
    service: "Business Growth Strategy",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-consistency-into-growth.jpg",
    headline: "Turning production inconsistency into scalable manufacturing performance",
    opportunity: [
      "As production volumes increased, maintaining consistent operational performance became increasingly difficult. Variations across planning, execution and fulfilment affected customer experience and business efficiency. The opportunity was to create a standardized operating approach that could support higher volumes without increasing complexity.",
    ],
    role: [
      "Simplified operational workflows across manufacturing.",
      "Improved visibility into day-to-day business performance.",
      "Enabled faster operational decision-making.",
      "Created a scalable framework for continuous growth.",
    ],
    metrics: [
      { metric: "22%", label: "growth in production efficiency" },
      { metric: "86% → 97%", label: "order fulfilment performance" },
    ],
    outcomes: [],
  },
  {
    slug: "performance-into-growth",
    kicker: "Performance into Growth",
    client: "Leading Footwear Manufacturer",
    service: "Business Growth Strategy",
    location: "India",
    situation: "Ready to Scale",
    image: "/assets/case-performance-into-growth.jpg",
    headline: "Turning production pressure into sustained business growth",
    opportunity: [
      "Growing customer demand and shorter delivery cycles increased pressure on manufacturing operations. Balancing productivity, quality and delivery commitments became increasingly challenging. The opportunity was to improve operational performance while building a stronger foundation for future business growth.",
    ],
    role: [
      "Identified operational challenges limiting business performance.",
      "Streamlined coordination across key manufacturing activities.",
      "Improved visibility to support better business decisions.",
      "Built a scalable operating model for sustainable growth.",
    ],
    metrics: [
      { metric: "26%", label: "improvement in manufacturing productivity" },
      { metric: "81% → 95%", label: "on-time delivery performance" },
    ],
    outcomes: [],
  },
  {
    slug: "style-into-scale",
    kicker: "Style into Scale",
    client: "Kids Apparel Startup",
    service: "Business Strategy \u00B7 Brand \u00B7 Omnichannel \u00B7 Product Development",
    location: "India, Tier 2 Cities",
    situation: "Ready to Scale",
    image: "/assets/case-style-into-scale.jpg",
    headline: "Turning ensemble dressing into a US$2.9M kidswear business",
    opportunity: [
      "Children's apparel buying was often fragmented: tops, bottoms, colours and styles were chosen separately, with little guidance on how they worked together as a complete look.",
      "The opportunity was to simplify that experience by introducing ensemble dressing: helping parents and children understand how individual pieces could be paired into a coordinated outfit.",
      "The ambition was not just to launch another kidswear label. It was to build an omnichannel apparel business that could popularise a new way of dressing children across Tier 2 markets in India, while connecting product design, retail, digital and partner-led expansion into one scalable model.",
    ],
    role: [
      "Developed the complete business blueprint, defining the commercial model, growth strategy and operating structure required to build the brand from the ground up",
      "Created the brand positioning around ensemble dressing, turning coordinated styling into a differentiated category proposition rather than competing only on fashion, price or individual garments",
      "Developed the brand identity and visual language to make the proposition easy to understand across stores, digital platforms and product communication",
      "Built the offline-to-online integration strategy so physical retail, digital discovery and customer engagement worked as one connected omnichannel experience",
      "Designed and developed the digital platform to support product discovery, outfit pairing and the broader customer journey",
      "Established the apparel design and development hub, creating a system for building coordinated collections rather than disconnected SKUs",
      "Structured product logic around complementary tops, bottoms, colours and styles so children and parents could more easily create complete ensembles",
      "Developed partner programmes and expansion frameworks to support rapid retail rollout across multiple states",
      "Connected business strategy, brand, product development, technology, retail and partnerships into one integrated growth engine",
    ],
    metrics: [
      { metric: "12 stores", label: "launched within 3 months across 4 states" },
      { metric: "US$2.9M+", label: "revenue generated over the first year" },
    ],
    outcomes: [
      "Built an omnichannel kidswear business around a differentiated ensemble-dressing proposition",
      "Created a scalable retail, digital and product-development model capable of rapid expansion across Tier 2 India",
    ],
  },
];

export const getCase = (slug) => CASE_STUDIES.find((c) => c.slug === slug);
